import { useCallback, useMemo, useTransition, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  QueryParamsAdapterProvider as BlocksQueryParamsAdapterProvider,
  type QueryParamsAdapter,
} from "@autodromo/blocks/hooks/queryParams/queryParamsAdapter";

/**
 * Injeta o adapter de query params dos blocos usando next/navigation.
 * O `next/*` fica CONFINADO ao renderer — os blocos federados não importam Next
 * (ver packages/blocks/src/hooks/queryParams/queryParamsAdapter.tsx).
 */ 
export function QueryParamsAdapterProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const pushQuery = useCallback(
    (queryString: string) => {
      startTransition(() => {
        router.push(`${pathname}${queryString ? `?${queryString}` : ""}`, {
          scroll: false,
        });
      });
    },
    [router, pathname],
  );

  // Troca a query só na URL, sem passar pelo router: o estado do Comparador
  // vive em React (o commit já faz setChosen), e o window.history.replaceState
  // não dispara routeChangeComplete, então não vira page_view no GA central nem
  // reseta a Listagem/DynamicList da mesma página (que escutam o router.asPath).
  const replaceQuery = useCallback((queryString: string) => {
    if (typeof window === "undefined") return;
    const url = `${window.location.pathname}${queryString ? `?${queryString}` : ""}${window.location.hash}`;
    window.history.replaceState(null, "", url);
  }, []);

  const value = useMemo<QueryParamsAdapter>(
    () => ({
      searchParams: new URLSearchParams(searchParams?.toString() ?? ""),
      pushQuery,
      replaceQuery,
      isPending,
    }),
    [searchParams, pushQuery, replaceQuery, isPending],
  );

  return (
    <BlocksQueryParamsAdapterProvider value={value}>
      {children}
    </BlocksQueryParamsAdapterProvider>
  );
}